import { useCallback, useRef, useState } from "react"

import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material"
import { Dropzone } from "../Dropzone"
import { VisuallyHiddenInput } from "../VisuallyHidenInput"
import { UploadSimple } from "@phosphor-icons/react"

import type { MutationStatus } from "@tanstack/react-query"

interface UploadSectionProps {
	uploadStatus: MutationStatus
	onFileUpload: (files: FileList) => void
	onFluxpromptUpload?: VoidFunction 
}

function UploadSection({ 
	uploadStatus,
	onFileUpload,
	onFluxpromptUpload
}: UploadSectionProps) {
	const [dragActive, setDragActive] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const handleDrag = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()

		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true)
		} else if (e.type === 'dragleave') {
			setDragActive(false)
		}
	}, [])

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setDragActive(false)

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			onFileUpload(e.dataTransfer.files)
		}
	}, [onFileUpload])

	const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			onFileUpload(e.target.files)
		}
	}, [onFileUpload])

	const handleClick = () => {
		fileInputRef.current?.click()
	}

	const uploadLabels = {
		idle: (
			<span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
				<UploadSimple size={20} color="#9ca3af" />
				Drag & Drop to upload (PDF, DOCX, or CSV, up to 10 MB)
			</span>
		),
		pending: (
			<span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
				<CircularProgress size={16} color="inherit" />
				Uploading files
			</span>
		),
		error: 'Error while uploading files, try again',
		success: 'Files uploaded successfully'
	}

	const isUploadInputDisabled = uploadStatus === 'pending'

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(7, 1fr)',
				gap: 2,
				flexDirection: { xs: 'column', md: 'row' },
			}}
		>
			<Dropzone
				isDragActive={dragActive}
				onClick={handleClick}
				onDragEnter={handleDrag}
				onDragLeave={handleDrag}
				onDragOver={handleDrag}
				onDrop={handleDrop}
				aria-disabled={isUploadInputDisabled}
			>
				<VisuallyHiddenInput
					ref={fileInputRef}
					type="file"
					// accept=".pdf,.docx,.csv"
					multiple
					disabled={isUploadInputDisabled}
					onChange={handleFileInput}
				/>

				<Typography
					variant="body1"
					sx={{
						color: '#6b7280',
						textAlign: 'center',
						fontSize: '0.875rem',
					}}
				>
					{uploadLabels[uploadStatus]}
				</Typography>
			</Dropzone>

			<Button
				component={Paper}
				onClick={onFluxpromptUpload}
				sx={{
					minWidth: 'auto !important',
					maxWidth: 'auto !important',

					width: '100%',
					height: 'auto !important',

					gridColumn: 'span 3',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '0.5rem',

					paddingInline: '1.5rem',
					paddingBlock: '1rem',

					borderStyle: 'solid',
					borderWidth: '1px',
					borderColor: '#d1d5db',
					borderRadius: '0.5rem !important',

					backgroundColor: '#fafafa',
					cursor: 'pointer',
					transition: 'all 0.2s ease-in-out',
					boxShadow: 'none',

					textTransform: 'none',
					color: '#6b7280',
					fontSize: '0.875rem',
					fontWeight: 400,

					pointerEvents: 'none',
					opacity: '50%',

					'&:hover': {
						borderColor: '#3b82f6',
						backgroundColor: '#eff6ff',
					},
				}}
			>
				<UploadSimple size={20} color="#9ca3af" />

				Upload From Fluxprompt
			</Button>
		</Box>
	)
}

export { UploadSection }